import { supabase } from "../lib/supabase";

export class ModelBuilderService {
  /**
   * Generate a human-readable rule name from filter criteria
   */
  static generateRuleName(filters) {
    const parts = [];

    if (filters.state_filter?.length) {
      parts.push(filters.state_filter.join("/") + " projects");
    }

    if (filters.acreage_min && filters.acreage_max) {
      parts.push(`between ${filters.acreage_min}-${filters.acreage_max} acres`);
    } else if (filters.acreage_min) {
      parts.push(`over ${filters.acreage_min} acres`);
    } else if (filters.acreage_max) {
      parts.push(`under ${filters.acreage_max} acres`);
    }

    if (filters.template_filters?.length) {
      const templates = filters.template_filters
        .map((t) => t.replace(/^\d+-/, ""))
        .join(" or ");
      parts.push(`with ${templates} templates`);
    }

    if (filters.client_names?.length) {
      parts.push(`for ${filters.client_names.join(", ")}`);
    }

    return parts.join(" ") || "All projects";
  }

  /**
   * Get all model builders (employees with 'Ready to Model' in status_mentions)
   */
  static async getModelBuilders() {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, status_mentions")
        .contains("status_mentions", ["Ready to Model"])
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching model builders:", error);
        throw new Error(`Failed to fetch model builders: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error in getModelBuilders:", error);
      throw error;
    }
  }

  /**
   * Get all assignment rules ordered by priority
   */
  static async getAssignmentRules() {
    try {
      const { data, error } = await supabase
        .from("model_builder_assignment_rules")
        .select("*")
        .order("priority", { ascending: true });

      if (error) {
        console.error("Error fetching assignment rules:", error);
        throw new Error(`Failed to fetch assignment rules: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error in getAssignmentRules:", error);
      throw error;
    }
  }

  /**
   * Create a new assignment rule
   */
  static async createAssignmentRule(ruleData) {
    try {
      // Get the next priority number
      const { data: maxPriorityData, error: maxError } = await supabase
        .from("model_builder_assignment_rules")
        .select("priority")
        .order("priority", { ascending: false })
        .limit(1)
        .single();

      const nextPriority =
        maxError || !maxPriorityData ? 1 : maxPriorityData.priority + 1;

      // Generate rule name
      const rule_name = this.generateRuleName(ruleData);

      const dbData = {
        priority: nextPriority,
        rule_name,
        state_filter: ruleData.state_filter || null,
        acreage_min: ruleData.acreage_min || null,
        acreage_max: ruleData.acreage_max || null,
        template_filters: ruleData.template_filters || null,
        client_ids: ruleData.client_ids || null,
        eligible_model_builder_ids: ruleData.eligible_model_builder_ids,
      };

      const { data, error } = await supabase
        .from("model_builder_assignment_rules")
        .insert([dbData])
        .select()
        .single();

      if (error) {
        console.error("Error creating assignment rule:", error);
        throw new Error(`Failed to create assignment rule: ${error.message}`);
      }

      // Recalculate eligibility for all Ready to Model projects
      await this.recalculateAllEligibility();

      return data;
    } catch (error) {
      console.error("Error in createAssignmentRule:", error);
      throw error;
    }
  }

  /**
   * Update an existing assignment rule
   */
  static async updateAssignmentRule(id, ruleData) {
    try {
      // Generate rule name
      const rule_name = this.generateRuleName(ruleData);

      const dbData = {
        rule_name,
        state_filter: ruleData.state_filter || null,
        acreage_min: ruleData.acreage_min || null,
        acreage_max: ruleData.acreage_max || null,
        template_filters: ruleData.template_filters || null,
        client_ids: ruleData.client_ids || null,
        eligible_model_builder_ids: ruleData.eligible_model_builder_ids,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("model_builder_assignment_rules")
        .update(dbData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating assignment rule:", error);
        throw new Error(`Failed to update assignment rule: ${error.message}`);
      }

      // Recalculate eligibility for all Ready to Model projects
      await this.recalculateAllEligibility();

      return data;
    } catch (error) {
      console.error("Error in updateAssignmentRule:", error);
      throw error;
    }
  }

  /**
   * Delete an assignment rule
   */
  static async deleteAssignmentRule(id) {
    try {
      const { error } = await supabase
        .from("model_builder_assignment_rules")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting assignment rule:", error);
        throw new Error(`Failed to delete assignment rule: ${error.message}`);
      }

      // Recalculate priorities to fill the gap
      await this.resequencePriorities();

      // Recalculate eligibility for all Ready to Model projects
      await this.recalculateAllEligibility();

      return true;
    } catch (error) {
      console.error("Error in deleteAssignmentRule:", error);
      throw error;
    }
  }

  /**
   * Reorder rules after drag-and-drop
   */
  static async reorderRules(rulesArray) {
    try {
      // Update each rule's priority based on array order
      const updates = rulesArray.map((rule, index) =>
        supabase
          .from("model_builder_assignment_rules")
          .update({ priority: index + 1, updated_at: new Date().toISOString() })
          .eq("id", rule.id),
      );

      const results = await Promise.all(updates);

      // Check for errors
      const errorResult = results.find((r) => r.error);
      if (errorResult?.error) {
        console.error("Error reordering rules:", errorResult.error);
        throw new Error(
          `Failed to reorder rules: ${errorResult.error.message}`,
        );
      }

      // Recalculate eligibility for all Ready to Model projects
      await this.recalculateAllEligibility();

      return true;
    } catch (error) {
      console.error("Error in reorderRules:", error);
      throw error;
    }
  }

  /**
   * Resequence priorities to be sequential (1, 2, 3, ...) after deletion
   */
  static async resequencePriorities() {
    try {
      const { data: rules, error } = await supabase
        .from("model_builder_assignment_rules")
        .select("id")
        .order("priority", { ascending: true });

      if (error) throw error;

      if (rules && rules.length > 0) {
        const updates = rules.map((rule, index) =>
          supabase
            .from("model_builder_assignment_rules")
            .update({ priority: index + 1 })
            .eq("id", rule.id),
        );

        await Promise.all(updates);
      }

      return true;
    } catch (error) {
      console.error("Error in resequencePriorities:", error);
      throw error;
    }
  }

  /**
   * Get eligibility data for a specific project
   */
  static async getProjectEligibility(projectId) {
    try {
      const { data, error } = await supabase
        .from("project_model_builder_eligibility")
        .select(
          `
          *,
          employee:employees!employee_id(id, name),
          assigned_by:employees!assigned_by_employee_id(id, name)
        `,
        )
        .eq("project_id", projectId);

      if (error) {
        console.error("Error fetching project eligibility:", error);
        throw new Error(
          `Failed to fetch project eligibility: ${error.message}`,
        );
      }

      return data || [];
    } catch (error) {
      console.error("Error in getProjectEligibility:", error);
      throw error;
    }
  }

  /**
   * Get all Ready to Model projects with their eligibility data
   */
  static async getReadyToModelProjectsWithEligibility() {
    try {
      // Fetch projects
      const { data: projects, error: projectsError } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          job_number,
          rev,
          location,
          approximate_acreage,
          template_filter,
          modeling_status,
          is_high_priority,
          is_soft_hold,
          due_date,
          client_id,
          clients(id, name)
        `,
        )
        .eq("modeling_status", "Ready to Model")
        .order("job_number", { ascending: false });

      if (projectsError) {
        console.error("Error fetching Ready to Model projects:", projectsError);
        throw new Error(`Failed to fetch projects: ${projectsError.message}`);
      }

      // Fetch eligibility for all projects
      const { data: eligibility, error: eligibilityError } = await supabase
        .from("project_model_builder_eligibility")
        .select(
          `
          *,
          employee:employees!employee_id(id, name)
        `,
        )
        .in(
          "project_id",
          projects.map((p) => p.id),
        );

      if (eligibilityError) {
        console.error("Error fetching eligibility:", eligibilityError);
        throw new Error(
          `Failed to fetch eligibility: ${eligibilityError.message}`,
        );
      }

      // Combine data and transform to camelCase to match App.js format
      const projectsWithEligibility = projects.map((project) => ({
        id: project.id,
        name: project.name,
        jobNumber: project.job_number,
        rev: project.rev,
        location: project.location,
        approximateAcreage: project.approximate_acreage,
        templateFilter: project.template_filter,
        modelingStatus: project.modeling_status,
        isHighPriority: project.is_high_priority,
        isSoftHold: project.is_soft_hold,
        dueDate: project.due_date,
        clientId: project.client_id,
        clientName: project.clients?.name || null,
        clients: project.clients,
        eligibility: eligibility.filter((e) => e.project_id === project.id),
      }));

      return projectsWithEligibility;
    } catch (error) {
      console.error("Error in getReadyToModelProjectsWithEligibility:", error);
      throw error;
    }
  }

  /**
   * Toggle manual assignment of a builder to a project
   */
  static async toggleBuilderAssignment(
    projectId,
    employeeId,
    assignedByEmployeeId,
    shouldAssign,
  ) {
    try {
      if (shouldAssign) {
        // Manually assign the builder
        const { data, error } = await supabase
          .from("project_model_builder_eligibility")
          .upsert(
            {
              project_id: projectId,
              employee_id: employeeId,
              is_eligible_by_rules: false, // Will be determined by trigger
              is_manually_assigned: true,
              assigned_by_employee_id: assignedByEmployeeId,
              assigned_at: new Date().toISOString(),
            },
            {
              onConflict: "project_id,employee_id",
            },
          )
          .select()
          .single();

        if (error) {
          console.error("Error assigning builder:", error);
          throw new Error(`Failed to assign builder: ${error.message}`);
        }

        return data;
      } else {
        // Unassign the builder (remove manual assignment)
        const { error } = await supabase
          .from("project_model_builder_eligibility")
          .update({
            is_manually_assigned: false,
            assigned_by_employee_id: null,
            assigned_at: null,
          })
          .eq("project_id", projectId)
          .eq("employee_id", employeeId);

        if (error) {
          console.error("Error unassigning builder:", error);
          throw new Error(`Failed to unassign builder: ${error.message}`);
        }

        // If they're not eligible by rules either, delete the record
        const { error: deleteError } = await supabase
          .from("project_model_builder_eligibility")
          .delete()
          .eq("project_id", projectId)
          .eq("employee_id", employeeId)
          .eq("is_eligible_by_rules", false);

        return true;
      }
    } catch (error) {
      console.error("Error in toggleBuilderAssignment:", error);
      throw error;
    }
  }

  /**
   * Recalculate eligibility for all Ready to Model projects
   */
  static async recalculateAllEligibility() {
    try {
      const { data, error } = await supabase.rpc(
        "recalculate_all_ready_to_model_eligibility",
      );

      if (error) {
        console.error("Error recalculating eligibility:", error);
        throw new Error(`Failed to recalculate eligibility: ${error.message}`);
      }

      console.log(`Recalculated eligibility for ${data} projects`);
      return data;
    } catch (error) {
      console.error("Error in recalculateAllEligibility:", error);
      throw error;
    }
  }
}
